import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import MailIcon from "@mui/icons-material/Mail";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { isHeaderLinkActive } from "./HeaderNavLinks";

const rowSx = { minHeight: 56, px: 2.5 };

/**
 * Phone header: one menu button that opens a drawer with every page link and the account items.
 *
 * @param {object} props
 * @param {{ label: string, path: string, icon: React.ElementType }[]} props.links
 * @param {string} props.userName
 * @param {string} props.initials
 * @param {number} props.notificationCount
 * @param {() => void} props.onLogout
 */
export default function HeaderMobileMenu({ links, userName, initials, notificationCount = 0, onLogout }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <IconButton size="large" edge="end" color="inherit" aria-label="Open menu" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <Badge badgeContent={notificationCount} color="error">
          <MenuIcon />
        </Badge>
      </IconButton>
      <Drawer anchor="right" open={open} onClose={close} PaperProps={{ sx: { width: "min(86vw, 340px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 2, color: "common.white", bgcolor: "secondary.main" }}>
          <Avatar sx={{ color: "secondary.main", bgcolor: "common.white" }}>{initials}</Avatar>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: "1rem", fontWeight: 700 }} noWrap>{userName}</Typography>
          <IconButton color="inherit" aria-label="Close menu" onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List component="nav" aria-label="Main" disablePadding>
          {links.map(({ label, path, icon: Icon }) => {
            const isActive = isHeaderLinkActive(path, pathname);
            return (
              <ListItemButton
                key={label}
                component={Link}
                to={path}
                selected={isActive}
                aria-current={isActive ? "page" : undefined}
                onClick={close}
                sx={rowSx}
              >
                <ListItemIcon><Icon color={isActive ? "primary" : "inherit"} /></ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: "1rem", fontWeight: isActive ? 750 : 550 }} />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
        <List disablePadding>
          <ListItemButton sx={rowSx} onClick={close}>
            <ListItemIcon><MailIcon /></ListItemIcon>
            <ListItemText primary="Messages" primaryTypographyProps={{ fontSize: "1rem" }} />
          </ListItemButton>
          <ListItemButton sx={rowSx} onClick={close}>
            <ListItemIcon>
              <Badge badgeContent={notificationCount} color="error"><NotificationsIcon /></Badge>
            </ListItemIcon>
            <ListItemText primary="Notifications" primaryTypographyProps={{ fontSize: "1rem" }} />
          </ListItemButton>
          <ListItemButton component={Link} to="/myprofile" sx={rowSx} onClick={close}>
            <ListItemIcon><SettingsIcon /></ListItemIcon>
            <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: "1rem" }} />
          </ListItemButton>
          <ListItemButton sx={rowSx} onClick={onLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: "1rem" }} />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
